/* eslint-disable jsx-a11y/anchor-is-valid */
import {useMemo, useState} from 'react';
import { Box, IconButton, Stack, Paper, Typography } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ThumbUpAltIcon from '@mui/icons-material/ThumbUpAlt';
import ThumbUpOffAltIcon from '@mui/icons-material/ThumbUpOffAlt';
import ThumbDownAltIcon from '@mui/icons-material/ThumbDownAlt';
import ThumbDownOffAltIcon from '@mui/icons-material/ThumbDownOffAlt';
import { MessageProps, SourceRef } from '../types';
import DOMPurify from "dompurify";
import { renderToStaticMarkup } from "react-dom/server";
import styles from "../css/chatBubble.module.css";

import ReactMarkdown from 'react-markdown';
import rehypeRaw from "rehype-raw";
import remarkMath from 'remark-math';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import theme from '../style/theme';
import { api } from '../api/streamFetch';
import { openAlert } from '../reducer/alert';
import { store } from '../store/AriStore';

type ChatBubbleProps = MessageProps & {
  onCitationClicked: (filePath: string) => void;
  variant: 'sent' | 'received';
};

type HtmlParsedAnswer = {
  answerHtml: string;
  citations: SourceRef[];
};

/** 백엔드가 본문에 심는 인용 마커. 예: `[[1]]` */
const CITATION_MARKER = /\[\[(\d+)\]\]/g;

/**
 * 스트리밍 도중 아직 완성되지 않은 마커 꼬리.
 * `[[`, `[[1`, `[[12]`, 그리고 단독 `[` 만 걸리고 `[2024]` 같은 본문은 걸리지 않는다.
 */
const INCOMPLETE_MARKER_TAIL = /(?:\[\[\d*\]?|\[)$/;

/** DOMPurify 허용 목록. QA_PROMPT가 요구하는 HTML 표와 치환 결과 <sup>이 살아남아야 한다 */
const SANITIZE_CONFIG = {
  ADD_TAGS: ['sup'],
  ADD_ATTR: ['title', 'class'],
};


/** 뷰어로 열 수 있는 첨부인지 (클릭 가능 여부) */
const isViewableFile = (fileName: string): boolean => {
  const lower = fileName.toLowerCase();
  return lower.endsWith('.pdf') || lower.endsWith('.png');
};

/** 빈 문자열은 평가 취소(초기화)를 뜻한다 — 백엔드가 setEvalReset으로 처리 */
type Evaluation = 'LIKE' | 'DISLIKE' | '';

/**
 * 응답 평가를 서버에 반영한다.
 * 스트리밍과 동일한 인증 경로(api → streamFetch → AuthService.getAccessToken)를 쓴다.
 * 예전 구현은 sessionStorage의 ID 토큰을 직접 실어보내 401을 조용히 삼켰다.
 */
const sendEvaluation = async (id: string, evaluation: Evaluation): Promise<boolean> => {
  try {
    await api.get(`/evaluation?id=${encodeURIComponent(id)}&isLike=${evaluation}`);
    return true;
  } catch (error) {
    console.error('응답 평가 전송 실패:', error);
    store.dispatch(openAlert({ message: '평가를 저장하지 못했습니다. 잠시 후 다시 시도해 주세요.' }));
    return false;
  }
};

/**
 * 본문의 `[[n]]` 마커를 sources 매핑으로 풀어 <sup> 각주로 치환한다.
 * 파일명은 백엔드가 JSON으로 따로 보내주므로 본문에서 파싱하지 않는다.
 */
const parseAnswerToHtml = (answer: string, sources: SourceRef[] = []): HtmlParsedAnswer => {
  const byIndex = new Map(sources.map(source => [source.index, source]));
  const used = new Map<number, SourceRef>();

  // 스트리밍 중 반쪽짜리 마커가 화면에 잠깐 노출되는 것을 막는다
  const visibleAnswer = answer.trimEnd().replace(INCOMPLETE_MARKER_TAIL, '');

  const replacedHtml = visibleAnswer.replace(CITATION_MARKER, (_match, digits: string) => {
      const source = byIndex.get(Number(digits));

      // 매핑에 없는 번호(모델 환각)는 렌더에서 지운다
      if (!source) return '';

      used.set(source.index, source);

      return renderToStaticMarkup(
          // eslint-disable-next-line jsx-a11y/anchor-is-valid
          <a className="supContainer" title={source.fileName}>
            <sup>{source.index}</sup>
          </a>
      );
  });

  return {
      answerHtml: DOMPurify.sanitize(replacedHtml, SANITIZE_CONFIG),
      citations: Array.from(used.values()).sort((a, b) => a.index - b.index),
  };
}


export default function ChatBubble(props: ChatBubbleProps) {
  const { content, variant, timestamp, sender, id, sources, onCitationClicked } = props;
  const isSent = variant === 'sent';
  
  const [isHovered, setIsHovered] = useState<boolean>(false);
  const [isLiked, setIsLiked] = useState<boolean>(false);
  const [isUnLiked, setIsUnLiked] = useState<boolean>(false);
  const [isCelebrated, setIsCelebrated] = useState<boolean>(false);
  const [isEvaluating, setIsEvaluating] = useState<boolean>(false);

  /**
   * 같은 버튼을 다시 누르면 평가를 취소한다.
   * 화면에는 먼저 반영하고, 전송이 실패하면 이전 상태로 되돌린다.
   */
  const handleEvaluation = async (target: 'LIKE' | 'DISLIKE') => {
    if (isEvaluating) return;

    const isCancel = target === 'LIKE' ? isLiked : isUnLiked;
    const evaluation: Evaluation = isCancel ? '' : target;

    const previousLiked = isLiked;
    const previousUnLiked = isUnLiked;

    setIsLiked(evaluation === 'LIKE');
    setIsUnLiked(evaluation === 'DISLIKE');
    setIsEvaluating(true);

    const succeeded = await sendEvaluation(id, evaluation);

    if (!succeeded) {
      setIsLiked(previousLiked);
      setIsUnLiked(previousUnLiked);
    }

    setIsEvaluating(false);
  };

  // answerHtml은 parseAnswerToHtml 안에서 DOMPurify로 정제된 결과다
  const parsedAnswer = useMemo(() => parseAnswerToHtml(content, sources), [content, sources]);

  const handleContentCopyClick = async () => {
    try {
      await navigator.clipboard.writeText(content);
      store.dispatch(openAlert({ message: '클립보드에 복사되었습니다!' }))
    } catch (err) {
      console.error('클립보드 복사 실패: ', err);
    }
    setIsCelebrated((prevState) => !prevState);
  };

  return (
    <Box key={id} sx={{ maxWidth: '60%', minWidth: 'auto' }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        spacing={2}
        sx={{ mb: 0.25 }}
      >
        <Typography variant='body1'>
          {sender === 'ARI' ? sender : sender}
        </Typography>
        <Typography variant='body1'>{timestamp}</Typography>
      </Stack>
      <Box
          sx={{ position: 'relative' }}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <Paper
            elevation={0}
            sx={{
              maxWidth: '800px',
              p: 1.25,
              color: isSent ? '#FFF' : '#000',
              borderRadius: 4,
              borderTopRightRadius: isSent ? 0 : 14,
              borderTopLeftRadius: isSent ? 14 : 0,
              backgroundColor: 
                isSent
                ? theme.palette.secondary.dark
                : 'background.body',
            }}
          > 
            { <ReactMarkdown
              className="z-ui-markdown"
              children={parsedAnswer.answerHtml}
              remarkPlugins={[remarkMath, remarkGfm, remarkBreaks]}
              rehypePlugins={[rehypeRaw]}
              components={{
                code({ node, inline, className, children, ...rest }: any) {
                  const match = /language-(\w+)/.exec(className || '')
                  
                  return !inline && match ? (
                    <SyntaxHighlighter
                      {...rest}
                      children={children}
                      language={match[1]}
                      PreTag="div"
                    />
                  ) : (
                    <code {...props} className={`code-line`}>
                      {children}
                    </code>
                  )
                }
              }}
            /> }
            {!!parsedAnswer.citations.length && (
                    <div>
                        <span className={styles.citationLearnMore}>참고자료 :</span>
                        {parsedAnswer.citations.map((source) => {
                            const viewable = isViewableFile(source.fileName);
                            return (
                                // eslint-disable-next-line jsx-a11y/anchor-is-valid
                                <div key={source.index} className={styles.citationItem}>
                                  <a
                                    className={styles.citation}
                                    title={source.fileName}
                                    onClick={() => viewable ? onCitationClicked(source.fileName) : null}
                                    style={{ cursor: viewable ? 'pointer' : 'default' }}
                                  >
                                    {`[${source.index}] ${source.fileName}`}
                                  </a>
                                </div>
                            );
                        })}
                        <p className={styles.feedbackHint}>
                          답변이 도움이 되셨나요? 👍 👎 로 만족도를 남겨주시면 아리가 더 나은 답변을 드릴 수 있어요
                        </p>
                    </div>
            )}
          </Paper>
          {(!isSent && id !== "DEFAULT") && (
            <Stack
              direction="row"
              justifyContent={isSent ? 'flex-end' : 'flex-start'}
              spacing={0.5}
              sx={{
                position: 'absolute',
                top: '50%',
                p: 1.5,
                ...(isSent
                  ? {
                      left: 0,
                      transform: 'translate(-100%, -50%)',
                    }
                  : {
                      right: 0,
                      transform: 'translate(100%, -50%)',
                    }),
              }}
            >
              <IconButton
                aria-label={isLiked ? '좋아요 취소' : '좋아요'}
                disabled={isEvaluating}
                onClick={() => handleEvaluation('LIKE')}
              >
                {isLiked ? <ThumbUpAltIcon/> : <ThumbUpOffAltIcon />}
              </IconButton>
              <IconButton
                aria-label={isUnLiked ? '싫어요 취소' : '싫어요'}
                disabled={isEvaluating}
                onClick={() => handleEvaluation('DISLIKE')}
              >
                {isUnLiked ? <ThumbDownAltIcon/> : <ThumbDownOffAltIcon />}
              </IconButton>
              <IconButton
                onClick={handleContentCopyClick}
              >
                {isCelebrated ? <ContentCopyIcon /> : <ContentCopyIcon />}
              </IconButton>
            </Stack>
          )}
        </Box>
    </Box>
  );
}
